import React, { useState, useEffect, useCallback } from 'react';
import * as Styles from './styles';
import { messages, rewardTags } from './messages';
import S3VideoPlayer from '@/components/s3VideoPlayer';
import { DeviceType } from '@/interfaces/video';
import { detectDeviceType } from '@/utils/video';
import { resolvePath } from '@/utils/resolvePath';
import PlayIcon from '@/assets/Icons/play-icon.svg';
import StarIcon from '@/assets/Icons/star.svg';
import SplitIcon from '@/assets/Icons/split.svg';
import CloseIcon from '@/assets/Icons/video-close.svg';
import ArrowLeftIcon from '@/assets/Icons/left-arrow.svg';
import ArrowRightIcon from '@/assets/Icons/arrow-right.svg';
import {
    createVideoSource,
    createVideoMetadata,
    createVideoConfig,
    createVideoEvents,
} from './util';
import {
    DarkButton,
    SecondaryButton,
} from '@acko-tech/building-blocks.ui.button';
import { GarageCardDetails } from '@/types';
import RewardIcon from '@/assets/Icons/reward-icon.svg';
import { getGarageRoutePath } from '@/components/fakeDoor/utils';
import { ROUTEMAP } from '@/components/fakeDoor/constants';
import Router from '@/components/common/router';
import { openMapLink } from '@/utils';

interface GarageCardProps {
    garageCardDetails?: GarageCardDetails[];
    garageCardHeading?: string;
    buttonLabel?: string;
    rewardTag?: string;
}

const GarageCard: React.FC<GarageCardProps> = ({
    garageCardDetails = [],
    garageCardHeading,
    buttonLabel,
    rewardTag,
}) => {
    const [deviceType, setDeviceType] = useState<DeviceType>(DeviceType.MOBILE);
    useEffect(() => {
        const detectedDeviceType = detectDeviceType() as DeviceType;
        setDeviceType(detectedDeviceType);
    }, []);

    if (!garageCardDetails || garageCardDetails.length === 0) {
        return null;
    }

    return (
        <>
            {garageCardDetails.map((garageDetail, garageIndex) => (
                <GarageCardItem
                    key={garageDetail.garageId || garageIndex}
                    garageDetail={garageDetail}
                    deviceType={deviceType}
                    showGarageSelectButton={garageCardDetails.length > 1}
                    isFirstGarage={garageIndex === 0}
                    garageCardHeading={garageCardHeading}
                    buttonLabel={buttonLabel}
                    rewardTag={rewardTag}
                />
            ))}
        </>
    );
};

interface GarageCardItemProps {
    garageDetail: GarageCardDetails;
    deviceType: DeviceType;
    showGarageSelectButton: boolean;
    isFirstGarage: boolean;
    garageCardHeading?: string;
    buttonLabel?: string;
    rewardTag?: string;
}

type CarouselItemType = {
    type: 'image' | 'video';
    url: string;
    videoUrl?: string;
};

const GarageCardItem: React.FC<GarageCardItemProps> = ({
    garageDetail,
    deviceType,
    showGarageSelectButton,
    isFirstGarage,
    garageCardHeading,
    buttonLabel,
    rewardTag,
}) => {
    const videoUrl = garageDetail.garageVideo;
    const [shouldPlay, setShouldPlay] = useState(false);
    const [isLightboxOpen, setIsLightboxOpen] = useState(false);
    const [lightboxIndex, setLightboxIndex] = useState(0);
    const [carouselItemWidth, setCarouselItemWidth] = useState(360);
    const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
    const containerRef = React.useRef<HTMLDivElement>(null);

    const carouselItems: CarouselItemType[] = garageDetail.imageUrls.map(
        (url) => ({
            type: 'image' as const,
            url: resolvePath(url),
        })
    );

    // Create unified array with video first (if exists), then images
    const allCarouselItems: CarouselItemType[] = videoUrl
        ? [{ type: 'video', url: videoUrl, videoUrl }, ...carouselItems]
        : carouselItems;

    const serviceableAreas = garageDetail.serviceableAreas || [];
    const lat = garageDetail.lat;
    const long = garageDetail.long;
    const mapLink = garageDetail.mapLink;

    // Calculate carousel item width based on container
    useEffect(() => {
        const updateCarouselWidth = () => {
            if (containerRef.current) {
                const width = containerRef.current.offsetWidth;
                setCarouselItemWidth(width);
            }
        };

        updateCarouselWidth();
        window.addEventListener('resize', updateCarouselWidth);

        return () => {
            window.removeEventListener('resize', updateCarouselWidth);
        };
    }, []);

    // Track current slide index based on scroll position
    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        const handleScroll = () => {
            const scrollLeft = container.scrollLeft;
            const itemWidth = carouselItemWidth;
            const currentIndex = Math.round(scrollLeft / itemWidth);
            setCurrentSlideIndex(currentIndex);
        };

        container.addEventListener('scroll', handleScroll);
        // Set initial index
        handleScroll();

        return () => {
            container.removeEventListener('scroll', handleScroll);
        };
    }, [carouselItemWidth]);

    useEffect(() => {
        if (!containerRef.current || !videoUrl) return;

        const videoElement = containerRef.current.querySelector('video');
        if (!videoElement) return;

        const handlePlay = () => setShouldPlay(true);
        const handlePause = () => setShouldPlay(false);

        videoElement.addEventListener('play', handlePlay);
        videoElement.addEventListener('pause', handlePause);

        return () => {
            videoElement.removeEventListener('play', handlePlay);
            videoElement.removeEventListener('pause', handlePause);
        };
    }, [videoUrl]);

    const handleCarouselItemClick = (
        index: number,
        event?: React.MouseEvent<HTMLDivElement>
    ) => {
        event?.stopPropagation();
        setLightboxIndex(index);
        setIsLightboxOpen(true);
    };

    const handleDotClick = (index: number) => {
        if (containerRef.current) {
            const scrollPosition = index * carouselItemWidth;
            containerRef.current.scrollTo({
                left: scrollPosition,
                behavior: 'smooth',
            });
        }
    };

    const handleLightboxClose = useCallback(() => {
        setIsLightboxOpen(false);
        setShouldPlay(false);
    }, []);

    const handleLightboxPrev = useCallback(() => {
        setLightboxIndex(
            (prevIndex) =>
                (prevIndex + allCarouselItems.length - 1) %
                allCarouselItems.length
        );
    }, [allCarouselItems.length]);

    const handleLightboxNext = useCallback(() => {
        setLightboxIndex(
            (prevIndex) => (prevIndex + 1) % allCarouselItems.length
        );
    }, [allCarouselItems.length]);

    const handleKeyDown = useCallback(
        (event: KeyboardEvent) => {
            if (!isLightboxOpen) return;
            if (event.key === 'Escape') {
                setIsLightboxOpen(false);
                setShouldPlay(false);
            } else if (event.key === 'ArrowLeft') {
                setLightboxIndex(
                    (prevIndex) =>
                        (prevIndex + allCarouselItems.length - 1) %
                        allCarouselItems.length
                );
            } else if (event.key === 'ArrowRight') {
                setLightboxIndex(
                    (prevIndex) => (prevIndex + 1) % allCarouselItems.length
                );
            }
        },
        [isLightboxOpen, allCarouselItems.length]
    );

    useEffect(() => {
        if (isLightboxOpen) {
            window.addEventListener('keydown', handleKeyDown);
            return () => {
                window.removeEventListener('keydown', handleKeyDown);
            };
        }
    }, [isLightboxOpen, handleKeyDown]);

    // Prevent body scroll when lightbox is open
    useEffect(() => {
        if (isLightboxOpen) {
            const originalOverflow = document.body.style.overflow;
            document.body.style.overflow = 'hidden';
            return () => {
                document.body.style.overflow = originalOverflow;
            };
        }
    }, [isLightboxOpen]);

    const videoSource = createVideoSource(videoUrl || '');
    const videoMetadata = createVideoMetadata();
    const videoConfig = createVideoConfig(shouldPlay);
    const videoEvents = createVideoEvents(setShouldPlay);

    const rating = garageDetail.rating;
    const garageName = garageDetail.garageName;
    const workingTime = garageDetail.workingTime;
    const locationInfo = garageDetail.locationInfo;

    const handleButtonClick = (garageId: string | null) => {
        const redURL = garageId
            ? getGarageRoutePath(garageId.toLowerCase())
            : `${ROUTEMAP.GARAGE_COMING_SOON}?pincode=${garageDetail.pincode as string}`;

        Router.replace(redURL);
    };

    return (
        <Styles.GarageCardWrapper>
            {garageCardHeading && isFirstGarage && (
                <Styles.GarageCardContentWrapper>
                    <Styles.GarageCardHeading>
                        <Styles.GarageCardHeadingRow>
                            <Styles.GarageCardTextContent>
                                {garageCardHeading}
                            </Styles.GarageCardTextContent>
                        </Styles.GarageCardHeadingRow>
                    </Styles.GarageCardHeading>
                </Styles.GarageCardContentWrapper>
            )}
            <Styles.GarageDetailsContainer>
                <Styles.GarageDetailsVideoContainer>
                    <Styles.CarouselTrack ref={containerRef}>
                        {videoUrl && (
                            <Styles.CarouselItem
                                $width={carouselItemWidth}
                                onClick={(e) => handleCarouselItemClick(0, e)}
                            >
                                <S3VideoPlayer
                                    video={videoSource}
                                    metadata={videoMetadata}
                                    config={videoConfig}
                                    events={videoEvents}
                                    deviceType={deviceType}
                                    isVisible={true}
                                    className="garage-card-video-player"
                                />
                                {isFirstGarage && (
                                    <Styles.RecommendedTagContainer
                                        $isRecommended={
                                            rewardTag === rewardTags.recommended
                                        }
                                    >
                                        <Styles.RecommendedTagWrapper>
                                            <Styles.RecommendedIconWrapper>
                                                <RewardIcon />
                                            </Styles.RecommendedIconWrapper>
                                            <Styles.RecommendedTagText>
                                                {rewardTag}
                                            </Styles.RecommendedTagText>
                                        </Styles.RecommendedTagWrapper>
                                    </Styles.RecommendedTagContainer>
                                )}
                                {!shouldPlay && (
                                    <Styles.PlayButtonOverlay>
                                        <PlayIcon />
                                    </Styles.PlayButtonOverlay>
                                )}
                            </Styles.CarouselItem>
                        )}

                        {carouselItems.map((item, index) => {
                            const actualIndex = videoUrl ? index + 1 : index;
                            return (
                                <Styles.CarouselItem
                                    key={`carousel-item-${index}`}
                                    $width={carouselItemWidth}
                                    onClick={(e) =>
                                        handleCarouselItemClick(actualIndex, e)
                                    }
                                >
                                    <img
                                        src={item.url}
                                        alt={`Garage image ${index + 1}`}
                                        loading={index === 0 ? 'eager' : 'lazy'}
                                    />
                                </Styles.CarouselItem>
                            );
                        })}
                    </Styles.CarouselTrack>
                    {allCarouselItems.length > 1 && (
                        <Styles.DotsContainerWrapper>
                            <Styles.DotsContainer>
                                {allCarouselItems.map((_, index) => (
                                    <Styles.Dot
                                        key={`dot-${index}`}
                                        className={
                                            index === currentSlideIndex
                                                ? 'active'
                                                : ''
                                        }
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleDotClick(index);
                                        }}
                                    />
                                ))}
                            </Styles.DotsContainer>
                        </Styles.DotsContainerWrapper>
                    )}
                </Styles.GarageDetailsVideoContainer>
                {serviceableAreas.length > 0 && (
                    <Styles.GarageServiceableAreasContainer>
                        <Styles.GarageServiceableAreasContainerWrapper>
                            <Styles.GarageServiceableAreaTextWrapper>
                                {[...serviceableAreas, ...serviceableAreas].map(
                                    (area, index) => (
                                        <Styles.GarageServiceableAreaText
                                            key={`${area}-${index}`}
                                        >
                                            {String(area)}
                                        </Styles.GarageServiceableAreaText>
                                    )
                                )}
                            </Styles.GarageServiceableAreaTextWrapper>
                        </Styles.GarageServiceableAreasContainerWrapper>
                    </Styles.GarageServiceableAreasContainer>
                )}
                <Styles.GarageInfoContainer>
                    <Styles.GarageInfoDetailsContainer>
                        <Styles.GarageInfoHeadingTextWrapper>
                            <Styles.StarIconWrapper>
                                <StarIcon />
                            </Styles.StarIconWrapper>
                            <Styles.GarageInfoHeadingText>
                                {rating}
                                <Styles.SplitIconContainer>
                                    <SplitIcon />
                                </Styles.SplitIconContainer>
                                {messages.ratingText}
                            </Styles.GarageInfoHeadingText>
                        </Styles.GarageInfoHeadingTextWrapper>
                        <Styles.GarageInfoDescriptionContainer>
                            <Styles.GarageInfoDescription>
                                {garageName}
                            </Styles.GarageInfoDescription>
                        </Styles.GarageInfoDescriptionContainer>
                    </Styles.GarageInfoDetailsContainer>
                    {workingTime && (
                        <Styles.GarageTimingContainer>
                            <Styles.GarageTimingHeading>
                                Opens {workingTime}
                            </Styles.GarageTimingHeading>
                        </Styles.GarageTimingContainer>
                    )}
                    <Styles.GarageMapContainer>
                        <Styles.GarageMapIframe
                            src={`https://www.google.com/maps?q=${lat},${long}&hl=en&z=16&output=embed`}
                            allowFullScreen
                            loading="lazy"
                            referrerPolicy="no-referrer-when-downgrade"
                        />
                        {mapLink && (
                            <Styles.GarageMapOverlay
                                onClick={() => openMapLink(mapLink)}
                            />
                        )}
                    </Styles.GarageMapContainer>
                    {locationInfo && (
                        <Styles.GarageMapInfoContainer>
                            <Styles.GarageMapInfoText>
                                {locationInfo}
                            </Styles.GarageMapInfoText>
                        </Styles.GarageMapInfoContainer>
                    )}
                    {showGarageSelectButton && mapLink && (
                        <Styles.GarageButtonContainer>
                            <SecondaryButton
                                onClick={() => openMapLink(mapLink)}
                            >
                                Get Directions
                            </SecondaryButton>
                            <DarkButton
                                onClick={() =>
                                    handleButtonClick(garageDetail.garageId)
                                }
                            >
                                {buttonLabel}
                            </DarkButton>
                        </Styles.GarageButtonContainer>
                    )}
                </Styles.GarageInfoContainer>
            </Styles.GarageDetailsContainer>

            {/* Lightbox Modal */}
            {isLightboxOpen && (
                <Styles.LightboxOverlay onClick={handleLightboxClose}>
                    <Styles.LightboxContainer
                        onClick={(e) => e.stopPropagation()}
                    >
                        <Styles.LightboxCloseButton
                            onClick={handleLightboxClose}
                        >
                            <CloseIcon />
                        </Styles.LightboxCloseButton>

                        {allCarouselItems.length > 1 && (
                            <>
                                <Styles.LightboxNavButton
                                    $position="left"
                                    onClick={handleLightboxPrev}
                                >
                                    <ArrowLeftIcon />
                                </Styles.LightboxNavButton>
                                <Styles.LightboxNavButton
                                    $position="right"
                                    onClick={handleLightboxNext}
                                >
                                    <ArrowRightIcon />
                                </Styles.LightboxNavButton>
                            </>
                        )}

                        <Styles.LightboxContent>
                            {allCarouselItems[lightboxIndex]?.type ===
                            'video' ? (
                                <S3VideoPlayer
                                    video={createVideoSource(
                                        allCarouselItems[lightboxIndex]
                                            .videoUrl ||
                                            allCarouselItems[lightboxIndex].url
                                    )}
                                    metadata={createVideoMetadata()}
                                    config={{
                                        ...createVideoConfig(true),
                                        muted: false,
                                        showVolume: true,
                                        controls: true,
                                    }}
                                    events={createVideoEvents(setShouldPlay)}
                                    deviceType={deviceType}
                                    isVisible={true}
                                    className="garage-card-lightbox-video-player"
                                />
                            ) : (
                                <Styles.LightboxImage
                                    src={allCarouselItems[lightboxIndex]?.url}
                                    alt={`Garage image ${lightboxIndex + 1}`}
                                />
                            )}
                        </Styles.LightboxContent>
                    </Styles.LightboxContainer>
                </Styles.LightboxOverlay>
            )}
        </Styles.GarageCardWrapper>
    );
};

export default GarageCard;
